<template>
  <v-container>
    <v-select
      persistent-hint
      label="Timeline Data ID"
      v-model="$data.$_currentTimelineDataId"
      v-bind:items="$_timelineLabelDataIdAndNames"
      v-on:keydown.stop
    >
    </v-select>
    <v-data-table
      dense hide-default-footer sort-by="begin"
      v-bind:headers="$_timelineSegmentHeaders"
      v-bind:items="$_currentTimelineSegmentItems"
      v-bind:items-per-page="$_currentTimelineSegmentItems.length"
    >
      <template v-slot:item="{ item, headers }">
        <tr v-on:click="$_onClickRow(item.timelineSegmentId)">
          <td
            v-for="{ value: dataKey } of headers"
            v-bind:key="dataKey"
          >
            <input-holder
              v-if="dataKey === 'beginMsec'"
              v-bind:value="item[dataKey]"
              v-bind:update="(value) => ($_changeBegin(item.timelineSegmentId, Number(value)))"
            >
              <template v-slot:default="{ value, input, blur }">
                <general-text-field
                  class="pt-0"
                  hide-details single-line dense type="number"
                  v-bind:value="value"
                  v-on:input="input"
                  v-on:blur="blur"
                  v-on:click.stop
                >
                </general-text-field>
              </template>
            </input-holder>

            <input-holder
              v-else-if="dataKey === 'endMsec'"
              v-bind:value="item[dataKey]"
              v-bind:update="(value) => ($_changeEnd(item.timelineSegmentId, Number(value)))"
            >
              <template v-slot:default="{ value, input, blur }">
                <general-text-field
                  class="pt-0"
                  hide-details single-line dense type="number"
                  v-bind:value="value"
                  v-on:input="input"
                  v-on:blur="blur"
                  v-on:click.stop
                >
                </general-text-field>
              </template>
            </input-holder>

            <div
              class="d-flex flex-nowrap"
              v-else-if="dataKey === null"
            >
              <v-btn
                icon small class="flex-nowrap"
                v-on:click.stop="$_openDialog(item.timelineSegmentId)"
              >
                <v-icon small>mdi-dock-window</v-icon>
              </v-btn>
            </div>

            <v-btn
              dense outlined small
              v-else-if="item[dataKey] === undefined"
              v-on:click.stop="$_initializeWithDefaultData(item.timelineSegmentId, dataKey)"
            >
              initialize
            </v-btn>

            <input-holder
              v-else-if="$_dataValueTypes[dataKey] === $_valueTypeEnum.boolean"
              v-bind:value="item[dataKey]"
              v-bind:update="(value) => ($_changeData(item.timelineSegmentId, dataKey, value))"
            >
              <template v-slot:default="{ value, input }">
                <v-checkbox
                  hide-details dense
                  v-bind:input-value="value"
                  v-on:change="input"
                  v-on:click.stop
                >
                </v-checkbox>
              </template>
            </input-holder>

            <input-holder
              v-else-if="$_dataValueTypes[dataKey] === $_valueTypeEnum.number"
              v-bind:value="item[dataKey]"
              v-bind:update="(value) => ($_changeData(item.timelineSegmentId, dataKey, Number(value)))"
            >
              <template v-slot:default="{ value, input, blur }">
                <general-text-field
                  class="pt-0"
                  hide-details single-line dense type="number"
                  v-bind:value="value"
                  v-on:input="input"
                  v-on:blur="blur"
                  v-on:click.stop
                >
                </general-text-field>
              </template>
            </input-holder>

            <input-holder
              v-else-if="$_dataValueTypes[dataKey] === $_valueTypeEnum.string"
              v-bind:value="item[dataKey]"
              v-bind:update="(value) => ($_changeData(item.timelineSegmentId, dataKey, value))"
            >
              <template v-slot:default="{ value, input, blur }">
                <general-text-field
                  class="pt-0"
                  hide-details single-line dense
                  v-bind:value="value"
                  v-on:input="input"
                  v-on:blur="blur"
                  v-on:click.stop
                >
                </general-text-field>
              </template>
            </input-holder>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { TimelineDataSet } from '../../modules/ScattData.js';
import { TimelineSegmentId } from '../../modules/ScattData.js';
import GeneralSegmentData from '../../modules/segmentDataTypes/GeneralSegmentData.js';
import InputHolder from '../Parts/Inputs/InputHolder.vue';
import GeneralTextField from '../Parts/Inputs/GeneralTextField.vue';
import EditorDefinition from '../../modules/EditorDefinition.js';

const valueTypeEnum = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
};

export const GeneralEditorDefinition = new EditorDefinition(
  'ScattGeneralEditor',
  'General Editor',
  timelineDataSet => {
    for (let timelineData of Object.values(timelineDataSet)) {
      if (timelineData.segmentDataType === GeneralSegmentData) return true;
    }
    return false;
  },
);

export default {
  components: {
    InputHolder,
    GeneralTextField,
  },

  props: {
    timelineDataSet: { type: TimelineDataSet },
  },

  data() {
    return {
      $_currentTimelineDataId: undefined,
    };
  },

  computed: {
    $_valueTypeEnum() {
      return valueTypeEnum;
    },

    $_timelineLabelDataIdAndNames() {
      let timelineLabelDataIdAndNames = [];
      for (let [ timelineDataId, timelineData ] of Object.entries(this.timelineDataSet)) {
        if (timelineData.segmentDataType === GeneralSegmentData) {
          timelineLabelDataIdAndNames.push({
            text: `[${timelineDataId}] ${timelineData.name}`,
            value: timelineDataId,
          });
        }
      }
      return timelineLabelDataIdAndNames;
    },

    $_generalDataKeys() {
      let generalDataKeySet = new Set();
      if (this.$data.$_currentTimelineDataId !== undefined) {
        let timelineData = this.timelineDataSet[this.$data.$_currentTimelineDataId];
        for (let segment of Object.values(timelineData.segments)) {
          for (let generalDataKey of Object.keys(segment.data)) {
            generalDataKeySet.add(generalDataKey);
          }
        }
      }
      return [ ...generalDataKeySet ];
    },

    $_dataValueTypes() {
      let valueTypes = {};
      for (let dataKey of this.$_generalDataKeys) {
        for (let timelineSegmentItem of this.$_currentTimelineSegmentItems) {
          if (Object.keys(timelineSegmentItem).includes(dataKey)) {
            let dataValue = timelineSegmentItem[dataKey];
            if (dataValue === undefined) continue;
            if ((typeof dataValue === 'string') || (dataValue instanceof String)) {
              valueTypes[dataKey] = valueTypeEnum.string;
              break;
            } else if (typeof dataValue === 'number') {
              valueTypes[dataKey] = valueTypeEnum.number;
              break;
            } else if (typeof dataValue === 'boolean') {
              valueTypes[dataKey] = valueTypeEnum.boolean;
              break;
            }
          }
        }
      }
      return valueTypes;
    },

    $_timelineSegmentHeaders() {
      return [
        { value: 'beginMsec', text: 'Begin' },
        { value: 'endMsec',   text: 'End' },
        ...this.$_generalDataKeys.map(dataKey => ({ value: dataKey, text: dataKey })),
        { value: null,  sortable: false },
      ];
    },

    $_currentTimelineSegmentItems() {
      let timelineSegmentItems = [];
      if (this.$data.$_currentTimelineDataId === undefined) return timelineSegmentItems;
      let timelineData = this.timelineDataSet[this.$data.$_currentTimelineDataId];
      for (let [ segmentId, segment ] of Object.entries(timelineData.segments)) {
        let timelineSegmentItem = {};
        timelineSegmentItem.beginMsec = segment.begin.msec;
        timelineSegmentItem.endMsec = segment.end.msec;
        timelineSegmentItem.timelineDataName = segment.name;
        for (let dataKey of this.$_generalDataKeys) {
          if (Object.keys(segment.data).includes(dataKey)) {
            timelineSegmentItem[dataKey] = segment.data[dataKey];
          }
        }
        timelineSegmentItem.timelineSegmentId = new TimelineSegmentId(this.$data.$_currentTimelineDataId, segmentId);
        timelineSegmentItems.push(timelineSegmentItem);
      }
      return timelineSegmentItems;
    },
  },

  inject: [
    'clearSelectedTimelineSegments',
    'selectTimelineSegments',
    'selectTimelineSegmentAndSeek',
    'invokeActionOnSelectedTimelineSegments',
    'setSegment',
  ],

  mounted() {
    if (this.$_timelineLabelDataIdAndNames.length > 0) {
      this.$data.$_currentTimelineDataId = this.$_timelineLabelDataIdAndNames[0].value;
    }
  },

  methods: {
    /* private */
    $_onClickRow(timelineSegmentId) { this.selectTimelineSegmentAndSeek(timelineSegmentId); },

    $_openDialog(timelineSegmentId) {
      this.clearSelectedTimelineSegments();
      this.selectTimelineSegments(timelineSegmentId);
      this.invokeActionOnSelectedTimelineSegments('editSegment');
    },

    $_changeSegment(timelineSegmentId, newSegment) {
      return this.setSegment(timelineSegmentId, GeneralSegmentData, newSegment);
    },

    $_changeBegin(timelineSegmentId, newBeginMsec) {
      let newSegment = this.timelineDataSet.getSegment(timelineSegmentId).clone();
      newSegment.begin.msec = newBeginMsec;
      return this.$_changeSegment(timelineSegmentId, newSegment);
    },

    $_changeEnd(timelineSegmentId, newEndMsec) {
      let newSegment = this.timelineDataSet.getSegment(timelineSegmentId).clone();
      newSegment.end.msec = newEndMsec;
      return this.$_changeSegment(timelineSegmentId, newSegment);
    },

    $_changeData(timelineSegmentId, dataKey, newValue) {
      let newSegment = this.timelineDataSet.getSegment(timelineSegmentId).clone();
      newSegment.data[dataKey] = newValue;
      return this.$_changeSegment(timelineSegmentId, newSegment);
    },

    $_initializeWithDefaultData(timelineSegmentId, dataKey) {
      let newSegment = this.timelineDataSet.getSegment(timelineSegmentId).clone();
      switch (this.$_dataValueTypes[dataKey]) {
        case valueTypeEnum.string:
          newSegment.data[dataKey] = '';
          break;
        case valueTypeEnum.number:
          newSegment.data[dataKey] = 0;
          break;
        case valueTypeEnum.boolean:
          newSegment.data[dataKey] = false;
          break;
        default:
          return;
      }
      return this.$_changeSegment(timelineSegmentId, newSegment);
    },
  },
};
</script>